/**
 * ContactUs Component
 *
 * Public contact form page. Collects first name, last name, email, optional
 * phone number, and a message, then POSTs to /api/contact. On success the
 * form resets and a confirmation alert appears. A sidebar card provides
 * alternative contact information (email, institution, external website link).
 *
 * Route: /contact
 */
import React, { useState, useRef } from 'react';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
} from 'react-bootstrap';
import axios from 'axios';

import SEO from '@/components/common/SEO';
import Captcha from '@/components/common/Captcha';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

import './ContactUs.css';

export default function ContactUs() {
    const { ecosystem, isPlatform } = useEcosystem();
    // At the apex we market the platform itself; in tenant mode we reference
    // the resolved ecosystem by name. The institution block below names the
    // platform operator (MSU Denver / C3 Lab) in both modes, which is correct:
    // that is who runs RILE Connect regardless of tenant.
    const orgLabel = isPlatform
        ? 'RILE Connect'
        : ecosystem?.name || 'RILE Connect';
    const ecosystemLink = isPlatform
        ? null
        : ecosystem?.branding?.socialLinks?.linkedin || null;
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
    });
    const [captchaToken, setCaptchaToken] = useState(null);
    const captchaRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // Update a single form field by name
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Submit the contact form to the backend API
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/contact`,
                { ...formData, captchaToken }
            );
            setSuccess(
                'Thank you for your message! We will get back to you soon.'
            );
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                message: '',
            });
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    'Failed to send message. Please try again.'
            );
        } finally {
            setLoading(false);
            captchaRef.current?.reset();
            setCaptchaToken(null);
        }
    };

    return (
        <>
            <SEO
                title="Contact Us"
                description={`Get in touch with the RILE Connect team. Questions about ${orgLabel}? We'd love to hear from you.`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Contact', path: '/contact' },
                ]}
            />
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <h1 className="text-primary mb-4">Contact Us</h1>
                        <p className="lead mb-5">
                            Have questions about {orgLabel}? We&apos;d love to
                            hear from you.
                        </p>

                        <Row>
                            {/* Contact form */}
                            <Col md={7}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4">
                                        {success && (
                                            <Alert variant="success">
                                                {success}
                                            </Alert>
                                        )}
                                        {error && (
                                            <Alert variant="danger">
                                                {error}
                                            </Alert>
                                        )}

                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            First Name *
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="firstName"
                                                            value={
                                                                formData.firstName
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            Last Name *
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="lastName"
                                                            value={
                                                                formData.lastName
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Email *</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Phone</Form.Label>
                                                <Form.Control
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="303-555-1234"
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-4">
                                                <Form.Label>
                                                    Message *
                                                </Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={5}
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </Form.Group>

                                            <div className="mb-3">
                                                <Captcha
                                                    ref={captchaRef}
                                                    onVerify={setCaptchaToken}
                                                    onExpired={() =>
                                                        setCaptchaToken(null)
                                                    }
                                                    onError={() =>
                                                        setCaptchaToken(null)
                                                    }
                                                />
                                            </div>

                                            <div className="d-grid d-md-block">
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    className="rounded-pill px-4"
                                                    disabled={
                                                        loading || !captchaToken
                                                    }
                                                >
                                                    {loading
                                                        ? 'Sending...'
                                                        : 'Send Message'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </Col>
                            {/* Alternative contact info sidebar */}
                            <Col md={5}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4">
                                        <h5 className="text-primary mb-3">
                                            Other Ways to Connect
                                        </h5>
                                        <p className="text-muted">
                                            <strong>Email:</strong>
                                            <br />
                                            contact@rileconnect.org
                                        </p>
                                        <p className="text-muted">
                                            <strong>Institution:</strong>
                                            <br />
                                            Metropolitan State University of
                                            Denver
                                            <br />
                                            Department of Computer Sciences
                                        </p>
                                        {ecosystemLink && (
                                            <p className="text-muted mb-0">
                                                <strong>Website:</strong>
                                                <br />
                                                <a
                                                    href={ecosystemLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {orgLabel}
                                                </a>
                                            </p>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
